/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useContext } from 'react';
import styled from 'styled-components';

import { NavigationLinks } from '@toucan/common/services/navigation/types';

import AccountIcon from '@toucan/client/global/icons/user.svg';

import {
    ApplicationContext,
    ModalSize,
    ProductCategory,
} from '@toucan/client/global/application';
import { MarketCode, MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';
import { UserContext } from '@toucan/client/global/user';

import { SocialChannels } from '@toucan/client/shared/components/social-channels';

import { LoginForm } from '@toucan/client/blocks/components/login-form';

import { getDefaultFooterLinks } from '@toucan/client/features/layout/helpers/footer-links';

import { Footer, FooterVariant } from '../../../footer';
import { CategoryList } from './components/category-list';
import { styles } from './mobile-main-nav.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface MobileMainNavProps {
    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    navigationLinks: NavigationLinks;
}

const StyledMobileMainNav = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const MobileMainNav: FunctionComponent<MobileMainNavProps> = ({
    navigationLinks,
    ...otherProps
}: MobileMainNavProps) => {
    const { t } = useTranslation();
    const router = useRouter();
    const { activeMarket } = useContext(MarketContext);
    const { setModalVisible, setModalContent, setModalSize, legalLinks } =
        useContext(ApplicationContext);
    const { isLoggedIn } = useContext(UserContext);

    const footerLinks = getDefaultFooterLinks(t, activeMarket, legalLinks);

    const categories = navigationLinks.categoryLinks;
    const brands = navigationLinks.featuredBrandLinks;

    /**
     * Map and transform ProductCategories for use in the mobile menu
     *
     * @param {ProductCategory} categories - The categories to map
     * @returns {ProductCategory} - The categories
     */
    function mapCategoriesForMobileMenu(
        categories: ProductCategory[]
    ): ProductCategory {
        return {
            id: null,
            title: t('Shop'),
            url: '',
            templatePath: '',
            categories,
        };
    }

    const searchSlug = t('search', { ns: 'urls' });
    const brandsSlug = t('brands', { ns: 'urls' });

    const brandsLinkProps =
        activeMarket.code === MarketCode.FR_FR
            ? {
                  id: null,
                  url: `/${activeMarket.slug}/${searchSlug}?q=*`,
                  templatePath: '/search?q=*',
              }
            : {
                  id: null,
                  url: `/${activeMarket.slug}/${brandsSlug}`,
                  templatePath: '/brands',
              };

    const mappedCategories = mapCategoriesForMobileMenu([
        ...(categories ?? []),
        {
            ...brandsLinkProps,
            title: t('Brands'),
            categories: brands,
        },
        {
            id: null,
            title: t('Original content'),
            url: `/${activeMarket.slug}/${t('content')}`,
            templatePath: '/content',
            categories: navigationLinks?.originalContentLinks.map(
                ({ urlSlug, name }) => {
                    const contentSlug = t('content', { ns: 'urls' });

                    return {
                        id: null,
                        title: name,
                        url: `/${activeMarket.slug}/${contentSlug}/${urlSlug}`,
                        templatePath: `/content/category?slug=${urlSlug}`,
                    };
                }
            ),
        },
    ]);

    function handleAccountClick() {
        if (isLoggedIn) {
            router.push('/account', `/${activeMarket.slug}/${t('account')}`);
            return;
        }

        setModalSize(ModalSize.SMALL);
        setModalContent(
            <LoginForm onLogInSuccess={() => setModalVisible(false)} />
        );
        setModalVisible(true);
    }

    return (
        <StyledMobileMainNav {...otherProps}>
            <CategoryList
                category={mappedCategories}
                data-testid="mobile-main-nav__categories-list"
                className="categories-list"
            />
            <div className="pre-footer">
                <div
                    className="pre-footer-account"
                    onClick={handleAccountClick}
                >
                    <Icon
                        className="pre-footer-account-icon"
                        variant={IconVariant.DECORATIVE}
                        size={IconSize.M}
                    >
                        <AccountIcon />
                    </Icon>

                    <span className="pre-footer-account-text">
                        {t('My account')}
                    </span>
                </div>
                <WrappedLink
                    prefetch={false}
                    href="/sell-form"
                    data-testid="mobile-main-nav__sell-trade-button"
                    as={`/${activeMarket.slug}/${t('sell-or-trade', {
                        ns: 'urls',
                    })}`}
                >
                    {t('Start selling/trading')} &gt;
                </WrappedLink>
            </div>
            <div className="footer">
                <SocialChannels
                    className="social-channels"
                    data-testid="mobile-main-nav__social-channels"
                />
                <Footer
                    variant={FooterVariant.MOBILE}
                    linkLists={footerLinks}
                    data-testid="mobile-main-nav__footer"
                />
            </div>
        </StyledMobileMainNav>
    );
};
